import { useState } from "react";
import { Transition } from "@headlessui/react";
import axios from "axios";

const inputClass =
  "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
const labelClass =
  "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

const API = "https://erp-xe9u.onrender.com/api";

export default function AppendixManagement({
  appendices,
  setAppendices,
  setDialog,
  isAddingAppendix,
  setIsAddingAppendix,
  currentAppendixPage,
  setCurrentAppendixPage,
}) {
  const [newAppendix, setNewAppendix] = useState({ title: "", file: null });
  const [appendicesPerPage] = useState(10);

  const isValid = () =>
    newAppendix.title.trim() !== "" && newAppendix.file !== null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setNewAppendix((p) => ({
      ...p,
      [name]: name === "file" ? files[0] : value,
    }));
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("title", newAppendix.title);
      formData.append("appendix", newAppendix.file);
      const response = await axios.post(
        `${API}/company-profile/opportunity-appendices`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setAppendices(response.data);
      setNewAppendix({ title: "", file: null });
      setIsAddingAppendix(false);
      setCurrentAppendixPage(1);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Appendix added!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to add appendix.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/company-profile/opportunity-appendices/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAppendices(appendices.filter((a) => a._id !== id));
      setCurrentAppendixPage(1);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Appendix deleted.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to delete appendix.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const totalPages = Math.ceil(appendices.length / appendicesPerPage);
  const start = (currentAppendixPage - 1) * appendicesPerPage;
  const paginated = appendices.slice(start, start + appendicesPerPage);

  return (
    <div>
      {/* Add form */}
      <Transition
        show={isAddingAppendix}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Appendix Title</label>
              <input
                type="text"
                name="title"
                value={newAppendix.title}
                onChange={handleChange}
                placeholder="e.g. Safety Standards 2024"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>PDF File</label>
              <label className="mt-1 flex items-center gap-3 px-4 py-2 bg-gray-700 border border-gray-600 border-dashed rounded-md cursor-pointer hover:border-orange-400 transition-colors">
                <svg
                  className="w-4 h-4 text-gray-500 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="text-sm text-gray-400">
                  {newAppendix.file ? newAppendix.file.name : "Choose PDF file"}
                </span>
                <input
                  type="file"
                  name="file"
                  accept="application/pdf"
                  onChange={handleChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setIsAddingAppendix(false);
                setNewAppendix({ title: "", file: null });
              }}
              className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!isValid()}
              className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add Appendix
            </button>
          </div>
        </div>
      </Transition>

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Title
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">
                Uploaded
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-5 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      className="w-8 h-8 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-sm text-gray-600">
                      No appendices uploaded yet.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((a) => (
                <tr
                  key={a._id}
                  className="hover:bg-gray-700/40 transition-colors group"
                >
                  <td className="px-5 py-3.5">
                    <a
                      href={a.url.endsWith(".pdf") ? a.url : `${a.url}.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 group/link"
                    >
                      <svg
                        className="w-4 h-4 text-gray-500 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm text-orange-400 group-hover/link:text-orange-300 transition-colors">
                        {a.title}
                      </span>
                    </a>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-xs text-gray-500">
                      {new Date(a.uploadedAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => handleDelete(a._id)}
                      className="opacity-0 group-hover:opacity-100 px-2.5 py-1 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-all border border-red-400/20 hover:border-red-500"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Page {currentAppendixPage} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentAppendixPage((p) => Math.max(1, p - 1))}
              disabled={currentAppendixPage === 1}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const s = Math.max(
                1,
                Math.min(currentAppendixPage - 2, totalPages - 4),
              );
              const page = s + i;
              return page <= totalPages ? (
                <button
                  key={page}
                  onClick={() => setCurrentAppendixPage(page)}
                  className={`w-8 h-8 text-xs font-medium rounded-md transition-colors ${currentAppendixPage === page ? "bg-orange-400 text-white border border-orange-400" : "border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700"}`}
                >
                  {page}
                </button>
              ) : null;
            })}
            <button
              onClick={() =>
                setCurrentAppendixPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentAppendixPage === totalPages}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
